import React from 'react'
import { ThingHandwriting, Arrow, ContentAttach, DocumentBasic } from '@fs/zion-icon'
import {
  IconButton,
  Row,
  useOverlay,
  useContainerWidth,
  H1,
  Subheading,
  Button,
  usePageWidth,
  Separator,
} from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { SocialMenu } from '@fs/zion-social'
import HandwritingOverlay from '../../../../components/handwriting/HandwritingOverlay'
import HiddenMenu from '../hiddenMenu/HiddenMenu'
import { goBackToGive } from '../../../../utils/reviewUtils'
import usePrisonAccess from '../../../../../Give/hooks/usePrisonAccess'

export default function HeaderBar({ loading, personId, maxTasks = 1, doneCount = 0, showRecordDetails = false }) {
  const [t] = useTranslation()
  const atWidth = useContainerWidth()
  const handwritingOverlay = useOverlay()
  const sharable = { url: 'https://www.familysearch.org/getinvolved/' }
  const pageWidth = usePageWidth()
  const isMobile = pageWidth({ default: true, md: false })

  const { accessFromPrison, isReady } = usePrisonAccess()

  const handleClickRecordDetails = async () => {
    window.open(`/ark:/61903/${personId}`, '_blank') || window.location.assign(`/ark:/61903/${personId}`)
  }

  const getTitle = () => {
    return maxTasks
      ? `${t('full-name-viewer.title-with-max-tasks', 'Full Name Review ({currentCount} of {maxTasks})', {
          currentCount: doneCount + 1,
          maxTasks,
        })}`
      : `${t('full-name-viewer.title', 'Full Name Review ({currentCount})', {
          currentCount: doneCount + 1,
        })}`
  }

  return (
    <Row alignY="middle">
      <Separator />
      <IconButton
        Icon={Arrow}
        iconDirection="backward"
        onClick={() => goBackToGive(accessFromPrison && isReady, 'fullNameReview')}
        title={t('task-viewer.get-involved-link', 'Back to Get Involved')}
        data-testid="back-to-get-invovled"
        linkName="Back To Give"
      />
      {atWidth({
        default: (
          <H1 size="H5" growX>
            {t('full-name-correction.infosheet-header', 'Full Name Review')}
            <Subheading>{doneCount + 1}</Subheading>
          </H1>
        ),
        xs: (
          <H1 size="H5" growX>
            {getTitle()}
          </H1>
        ),
      })}
      {atWidth({
        default: (
          <Row alignX="end" growX>
            <IconButton
              Icon={ThingHandwriting}
              title={t('task-viewer.handwriting-tooltip', 'Handwriting Examples')}
              onClick={() => handwritingOverlay.handleClick()}
              data-testid="handwriting-help"
              linkName="Handwriting Help"
            />
            {!accessFromPrison && isReady && <SocialMenu disabled={loading} sharable={sharable} useIconButton />}
            <HiddenMenu
              loading={loading}
              personId={personId}
              showRecordDetails={showRecordDetails}
              handleClickRecordDetails={handleClickRecordDetails}
            />
          </Row>
        ),
        sm: (
          <Row alignX="end" growX>
            {personId &&
              showRecordDetails &&
              !accessFromPrison &&
              isReady &&
              (isMobile ? (
                <IconButton
                  disabled={loading}
                  Icon={DocumentBasic}
                  onClick={() => handleClickRecordDetails()}
                  data-testid="record-details"
                  linkName="Record Details"
                  title={t('navigation.details-button', 'Record Details')}
                />
              ) : (
                <Button
                  disabled={loading}
                  Icon={DocumentBasic}
                  onClick={() => handleClickRecordDetails()}
                  data-testid="record-details"
                  linkName="Record Details"
                >
                  {t('navigation.details-button', 'Record Details')}
                </Button>
              ))}
            {personId &&
              showRecordDetails &&
              !accessFromPrison &&
              isReady &&
              (isMobile ? (
                <IconButton
                  emphasis="medium"
                  disabled={loading}
                  Icon={ContentAttach}
                  onClick={() => {
                    window.open(`/search/linker?ark=${encodeURIComponent(`/ark:/61903/${personId}`)}`)
                  }}
                  data-testid="attach-to-tree"
                  linkName="Attach To Tree"
                  title={t('attach.header', 'Attach To Tree')}
                />
              ) : (
                <Button
                  emphasis="high"
                  disabled={loading}
                  Icon={ContentAttach}
                  onClick={() => {
                    window.open(`/search/linker?ark=${encodeURIComponent(`/ark:/61903/${personId}`)}`)
                  }}
                  data-testid="attach-to-tree"
                  linkName="Attach To Tree"
                >
                  {t('attach.header', 'Attach To Tree')}
                </Button>
              ))}
            <IconButton
              Icon={ThingHandwriting}
              title={t('task-viewer.handwriting-tooltip', 'Handwriting Examples')}
              onClick={() => handwritingOverlay.handleClick()}
              data-testid="handwriting-help"
              linkName="Handwriting Help"
            />
            {!accessFromPrison && isReady && <SocialMenu disabled={loading} sharable={sharable} useIconButton />}
          </Row>
        ),
      })}
      <HandwritingOverlay {...handwritingOverlay} />
      <Separator />
    </Row>
  )
}
